import React, { useState, useEffect } from 'react';
import { X, Save, Plus, Edit, Trash2, Wrench, Shield, Leaf, Settings } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { BusService } from '../../services/busService';
import { BusFeature, CreateFeatureForm, UpdateFeatureForm } from '../../types';

interface FeatureModalProps {
  onClose: () => void;
  onRefresh: () => void;
}

export const FeatureModal: React.FC<FeatureModalProps> = ({ onClose, onRefresh }) => {
  const [features, setFeatures] = useState<BusFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFeature, setEditingFeature] = useState<BusFeature | null>(null);
  const [formData, setFormData] = useState<CreateFeatureForm>({
    name: '',
    description: '',
    category: 'performance',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const categoryIcons = {
    performance: Settings,
    safety: Shield,
    environmental: Leaf,
    maintenance: Wrench,
  };

  const categoryColors = {
    performance: 'bg-blue-100 text-blue-800',
    safety: 'bg-red-100 text-red-800',
    environmental: 'bg-green-100 text-green-800',
    maintenance: 'bg-orange-100 text-orange-800',
  };

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      setLoading(true);
      const featureList = await BusService.getFeatures();
      setFeatures(featureList);
    } catch (error: any) {
      console.error('Failed to load features:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingFeature(null);
    setFormData({
      name: '',
      description: '',
      category: 'performance',
    });
    setErrors({});
    setShowForm(true);
  };

  const handleEdit = (feature: BusFeature) => {
    setEditingFeature(feature);
    setFormData({
      name: feature.name,
      description: feature.description,
      category: feature.category,
    });
    setErrors({});
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this feature?')) {
      try {
        // Note: In a real app, you'd need a delete method in BusService
        console.log('Delete feature:', id);
        await loadFeatures();
        onRefresh();
      } catch (error: any) {
        console.error('Failed to delete feature:', error);
      }
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      if (editingFeature) {
        await BusService.updateFeature(editingFeature.id, formData as UpdateFeatureForm);
      } else {
        await BusService.createFeature(formData);
      }
      
      await loadFeatures();
      onRefresh();
      setShowForm(false);
      setEditingFeature(null);
    } catch (error: any) {
      console.error('Failed to save feature:', error);
    }
  };

  const groupedFeatures = features.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = [];
    }
    acc[feature.category].push(feature);
    return acc;
  }, {} as Record<string, BusFeature[]>);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-2xl">
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-secondary-600">Loading features...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Wrench className="h-6 w-6 text-primary-600 mr-3" />
                <h2 className="text-xl font-semibold text-secondary-900">
                  {editingFeature ? 'Edit Feature' : 'Create Feature'}
                </h2>
              </div>
              <Button variant="outline" onClick={() => setShowForm(false)} className="p-2">
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., GPS Tracking, Anti-lock Brakes, Eco Mode"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this feature..."
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.description ? 'border-red-500' : 'border-secondary-300'
                  }`}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="performance">Performance</option>
                  <option value="safety">Safety</option>
                  <option value="environmental">Environmental</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-secondary-200">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="flex items-center">
                  <Save className="h-4 w-4 mr-2" />
                  {editingFeature ? 'Update Feature' : 'Create Feature'}
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Wrench className="h-6 w-6 text-primary-600 mr-3" />
              <h2 className="text-xl font-semibold text-secondary-900">Bus Features</h2>
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={handleCreate} className="flex items-center">
                <Plus className="h-4 w-4 mr-2" />
                Add Feature
              </Button>
              <Button variant="outline" onClick={onClose} className="p-2">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {features.length === 0 ? (
            <div className="text-center py-8">
              <Wrench className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 mb-2">No features found</h3>
              <p className="text-secondary-500 mb-4">Create your first feature to get started.</p>
              <Button onClick={handleCreate} className="flex items-center mx-auto">
                <Plus className="h-4 w-4 mr-2" />
                Add Feature
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => {
                const Icon = categoryIcons[category as keyof typeof categoryIcons];
                const colorClass = categoryColors[category as keyof typeof categoryColors];
                
                return (
                  <div key={category}>
                    <div className="flex items-center mb-4">
                      <Icon className="h-5 w-5 text-secondary-600 mr-2" />
                      <h3 className="text-lg font-semibold text-secondary-900 capitalize">
                        {category}
                      </h3>
                      <span className={`ml-3 px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
                        {categoryFeatures.length} feature{categoryFeatures.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {categoryFeatures.map((feature) => (
                        <Card key={feature.id} className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center">
                              <div className="p-2 bg-primary-100 rounded-lg mr-3">
                                <Icon className="h-4 w-4 text-primary-600" />
                              </div>
                              <div>
                                <h4 className="font-medium text-secondary-900">{feature.name}</h4>
                                <p className="text-sm text-secondary-600">{feature.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(feature)}
                                className="p-2"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(feature.id)}
                                className="p-2 text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
                              {feature.category}
                            </span>
                            <span className={`text-xs font-medium ${
                              feature.isActive ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {feature.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
